import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useMutation } from '@apollo/client';
import { PlanStep, StepStatus, IntakeStatus, ChatMessage, ChatRole, RequiredItemStatus, RequiredItemCategory, Participant, PlanRole } from '../../../types/generated/graphql';
import { colors } from '../../../theme/colors';
import { getStepTypeInfo, resolveStepAssignee } from '../../../utils/planHelpers';
import { getStepPanelMode } from '../../../utils/stepPanelMode';
import { FeedItem } from './StepInteractionFeed';
import StatusSummaryLine from './StatusSummaryLine';
import CopilotInputBar, { CopilotInput } from './CopilotInputBar';
import { ActiveWorkContent, WaitingOnDelegateContent, WaitingExternalContent, ReadyToCompleteContent, CompletedContent, ConnectionRequiredContent, shouldShowActionPrompt } from './step-modes';
import GravatarAvatar from '../../GravatarAvatar';
import { RESET_STEP, GET_PLAN, GET_CONVERSATION } from '../../../graphql/queries';
import ExpandStepButton from './ExpandStepButton';
import { useToast } from '../../../contexts/ToastContext';

interface StepWorkspacePaneProps {
  planId: string;
  step: PlanStep | null;
  participants?: Participant[];
  roles?: PlanRole[];
  messages?: ChatMessage[];
  onSendMessage: (text: string) => void;
  onToggleCompletion?: (stepId: string, completed: boolean) => void;
  isChatLoading?: boolean;
  /** All steps for finding next incomplete step */
  allSteps?: PlanStep[];
  /** Callback to select/navigate to a step */
  onSelectStep?: (stepId: string) => void;
}

// Convert step data and messages into unified feed items
// Note: Description is now shown in CollapsibleStepInfo, not in the feed
const buildFeedItems = (step: PlanStep, messages?: ChatMessage[]): FeedItem[] => {
  const items: FeedItem[] = [];
  const now = new Date().toISOString();

  // 1. Notes from user
  if (step.context?.notes) {
    items.push({
      id: `${step.id}-notes`,
      type: 'user_message',
      actor: 'user',
      timestamp: now,
      content: `**Notes:** ${step.context.notes}`,
    });
  }

  // 3. Links
  if (step.context?.links && step.context.links.length > 0) {
    items.push({
      id: `${step.id}-links`,
      type: 'user_message',
      actor: 'user',
      timestamp: now,
      content: `**Links:**\n${step.context.links.join('\n')}`,
    });
  }

  // 4. Intake / Data Collection as artifact
  if (step.intake) {
    const intakeContent = step.intake.status === IntakeStatus.Completed
      ? `Created request: "${step.intake.summary || 'Data collection'}"\nStatus: Completed`
      : `Created request: "${step.intake.summary || 'Data collection'}"\nStatus: Pending`;

    items.push({
      id: `${step.id}-intake`,
      type: 'artifact_request',
      actor: 'agent',
      timestamp: now,
      content: intakeContent,
      meta: {
        status: step.intake.status,
        type: 'Data Collection Form',
      },
    });

    // If intake has results, add participant response
    if (step.intake.normalizedResult || step.intake.rawText) {
      items.push({
        id: `${step.id}-intake-response`,
        type: 'participant_message',
        actor: 'participant',
        timestamp: now,
        content: step.intake.rawText || JSON.stringify(step.intake.normalizedResult, null, 2),
      });
    }
  }

  // 7. Execution Results as agent actions
  if (step.executionResults && step.executionResults.length > 0) {
    step.executionResults.forEach((result, idx) => {
      const resultStatus = (result as Record<string, unknown>).status;
      const resultOutput = (result as Record<string, unknown>).output;
      items.push({
        id: `${step.id}-execution-${idx}`,
        type: 'agent_action',
        actor: 'agent',
        timestamp: now,
        content: `Execution ${resultStatus === 'SUCCESS' ? 'completed' : 'failed'}${resultOutput ? `:\n${typeof resultOutput === 'string' ? resultOutput : JSON.stringify(resultOutput)}` : ''}`,
        meta: { status: resultStatus },
      });
    });
  }

  // 8. Chat messages - filter to those relevant to this step
  if (messages && messages.length > 0) {
    const stepPrefix = `[Step: ${step.title}]`;
    const relevantIndices = new Set<number>();

    messages.forEach((msg, idx) => {
      if (msg.role === ChatRole.User && msg.content?.includes(stepPrefix)) {
        relevantIndices.add(idx);
        if (idx + 1 < messages.length && messages[idx + 1].role === ChatRole.Assistant) {
          relevantIndices.add(idx + 1);
        }
      }
    });

    messages.forEach((msg, idx) => {
      if (!relevantIndices.has(idx)) return;

      const displayContent = msg.role === ChatRole.User
        ? msg.content?.replace(`${stepPrefix}\n\n`, '') || ''
        : msg.content || '';

      items.push({
        id: msg.id,
        type: msg.role === ChatRole.Assistant ? 'assistant_message' : 'user_message',
        actor: msg.role === ChatRole.Assistant ? 'planapus' : 'user',
        timestamp: msg.timestamp || now,
        content: displayContent,
      });
    });
  }

  // 9. Step Assessment (if available) - simplified for feed
  // Skip for completed steps (redundant with completion status)
  if (step.assessment && step.status !== StepStatus.Completed) {
    const assessment = step.assessment;
    const progress = assessment.progress;
    const structured = progress?.structured;
    const effortWeighted = progress?.effortWeighted;

    const progressPercent = structured
      ? Math.round(structured.fraction * 100)
      : Math.round((effortWeighted?.score || 0) * 100);

    let assessmentContent = `**Progress Check** - ${progressPercent}% complete`;

    // Only show "Ready to mark complete!" for non-completed steps
    if (assessment.eligibleForCompletion) {
      assessmentContent += '\n\n**Ready to mark complete!**';
    }

    // Show required items
    if (structured?.requiredItems && structured.requiredItems.length > 0) {
      const requiredItems = structured.requiredItems.filter(
        (i) => i.category === RequiredItemCategory.Required || !i.category
      );

      if (requiredItems.length > 0) {
        assessmentContent += '\n\n**Requirements:**';
        for (const item of requiredItems) {
          const statusIcon = item.status === RequiredItemStatus.Done ? '✓' :
                            item.status === RequiredItemStatus.Partial ? '~' : '○';
          assessmentContent += `\n${statusIcon} ${item.label}`;
        }
      }
    }

    items.push({
      id: `${step.id}-assessment-${assessment.id}`,
      type: 'assessment',
      actor: 'planapus',
      timestamp: assessment.timestamp || now,
      content: assessmentContent,
    });
  }

  // 10. Completion status - skip for completed steps
  // (CompletedContent handles this, no need for redundant system message)

  return items;
};

export const StepWorkspacePane: React.FC<StepWorkspacePaneProps> = ({
  planId,
  step,
  participants = [],
  roles = [],
  messages = [],
  onSendMessage,
  onToggleCompletion,
  isChatLoading = false,
  allSteps = [],
  onSelectStep,
}) => {
  const { showToast } = useToast();
  // Resolve step assignment using role-based logic
  const assignment = useMemo(() => {
    if (!step) return { participant: null, role: null, source: 'none' as const };
    return resolveStepAssignee(step, roles, participants);
  }, [step, roles, participants]);

  // For backward compatibility where just the participant is needed
  const assignee = assignment.participant;

  // Find next incomplete step (for "What's next" in completed mode)
  const nextIncompleteStep = useMemo(() => {
    if (!step || step.status !== StepStatus.Completed) return null;

    // Find the current step's index
    const currentIndex = allSteps.findIndex((s) => s.id === step.id);
    if (currentIndex === -1) return null;

    // Look for next incomplete step after current
    for (let i = currentIndex + 1; i < allSteps.length; i++) {
      const s = allSteps[i];
      if (s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped) {
        return { id: s.id, title: s.title };
      }
    }

    // If nothing after, look from beginning
    for (let i = 0; i < currentIndex; i++) {
      const s = allSteps[i];
      if (s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped) {
        return { id: s.id, title: s.title };
      }
    }

    return null;
  }, [step, allSteps]);

  // Reset step mutation
  const [resetStep, { loading: isResetting }] = useMutation(RESET_STEP, {
    refetchQueries: [
      { query: GET_PLAN, variables: { id: planId } },
      { query: GET_CONVERSATION, variables: { planId } },
    ],
  });

  // Build feed items from step data and messages
  const feedItems = useMemo(
    () => (step ? buildFeedItems(step, messages) : []),
    [step, messages]
  );

  // Get panel mode
  const panelMode = useMemo(() => getStepPanelMode(step), [step]);

  // Reset step to initial state (debug only)
  const handleResetStep = useCallback(async () => {
    if (!step || !planId) return;

    const confirmed = window.confirm(
      `Reset "${step.title}" to initial state?\n\nThis will clear all notes, context, assessment, and conversation history for this step.`
    );

    if (!confirmed) return;

    try {
      await resetStep({
        variables: { planId, stepId: step.id },
      });
      showToast({ message: 'Step has been reset', severity: 'success', autoHideDuration: 2000 });
    } catch (err) {
      console.error('Failed to reset step:', err);
      showToast({ message: 'Failed to reset step', severity: 'error' });
    }
  }, [step, planId, resetStep, showToast]);

  // Handle mark complete - also auto-advance to next step
  // Must be defined before early return to satisfy hooks rules
  const handleMarkComplete = useCallback(() => {
    if (!onToggleCompletion || !step) return;
    
    const wasCompleted = step.status === StepStatus.Completed;
    onToggleCompletion(step.id, !wasCompleted);
    
    // If we just completed the step (not uncompleting), navigate to next
    if (!wasCompleted && onSelectStep && allSteps.length > 0) {
      // Find next incomplete step
      const currentIndex = allSteps.findIndex((s) => s.id === step.id);
      if (currentIndex !== -1) {
        // Look for next incomplete step after current
        for (let i = currentIndex + 1; i < allSteps.length; i++) {
          const s = allSteps[i];
          if (s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped) {
            // Small delay to let the UI update
            setTimeout(() => {
              onSelectStep(s.id);
            }, 300);
            return;
          }
        }
        // If nothing after, look from beginning
        for (let i = 0; i < currentIndex; i++) {
          const s = allSteps[i];
          if (s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped) {
            setTimeout(() => {
              onSelectStep(s.id);
            }, 300);
            return;
          }
        }
      }
    }
  }, [step, onToggleCompletion, onSelectStep, allSteps]);

  // Empty state when no step is selected
  if (!step) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.card,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          p: 4,
          minHeight: 300,
        }}
      >
        <AssignmentIcon
          sx={{
            fontSize: 64,
            color: colors.gray[300],
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: colors.textSecondary,
            fontWeight: 500,
            mb: 1,
          }}
        >
          Select a step
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.textSecondary,
            textAlign: 'center',
            maxWidth: 300,
          }}
        >
          Click on a step from the list to view details and work on it with AI assistance.
        </Typography>
      </Box>
    );
  }

  const isCompleted = step.status === StepStatus.Completed;
  const stepTypeInfo = getStepTypeInfo(step);

  // Handle copilot send
  const handleCopilotSend = (input: CopilotInput) => {
    if (input.text) {
      onSendMessage(input.text);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.card,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Compact Step Header - Title + Status badges + Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: isCompleted ? 0.75 : 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            flex: 1,
            color: isCompleted ? colors.gray[600] : colors.textPrimary,
          }}
        >
          {step.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
          {/* For completed: show avatar + Done in one compact row */}
          {isCompleted ? (
            <>
              {assignee && (
                <GravatarAvatar
                  email={assignee.email}
                  name={assignee.name}
                  size={22}
                  showTooltip={true}
                />
              )}
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                label="Done"
                size="small"
                sx={{
                  height: 22,
                  bgcolor: colors.green[50],
                  color: colors.green[600],
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: colors.green[600] },
                  '& .MuiChip-label': { fontSize: '0.7rem' },
                }}
              />
            </>
          ) : (
            <>
              {/* Assignee chip - only for non-completed */}
              {assignee && (
                <Chip
                  avatar={
                    <GravatarAvatar
                      email={assignee.email}
                      name={assignee.name}
                      size={24}
                      showTooltip={false}
                    />
                  }
                  label={assignee.name}
                  size="small"
                  sx={{
                    height: 28,
                    bgcolor: colors.blue[50],
                    color: colors.blue[700],
                    fontWeight: 500,
                    '& .MuiChip-avatar': { ml: 0.25 },
                    '& .MuiChip-label': { pl: 0.5 },
                  }}
                />
              )}
              {/* Step type chip (if applicable) */}
              {stepTypeInfo && (
                <Chip
                  label={stepTypeInfo.label}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: stepTypeInfo.bgcolor,
                    color: stepTypeInfo.color,
                    fontWeight: 500,
                  }}
                />
              )}
            </>
          )}
          {/* Debug: Reset step button - only in development */}
          {process.env.NODE_ENV === 'development' && (
            <Tooltip title="Reset step (debug)">
              <IconButton
                size="small"
                onClick={handleResetStep}
                disabled={isResetting}
                sx={{
                  color: colors.amber[600],
                  '&:hover': { color: colors.amber[600], bgcolor: colors.amber[50] },
                }}
              >
                <RestartAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Status Summary Line */}
      <StatusSummaryLine step={step} panelMode={panelMode} participants={participants} roles={roles} />

      {/* Mode-specific content */}
      {panelMode === 'ACTIVE_WORK' && (
        <ActiveWorkContent
          planId={planId}
          step={step}
          participants={participants}
          roles={roles}
          feedItems={feedItems}
          onMarkComplete={handleMarkComplete}
          onSendMessage={onSendMessage}
          isChatLoading={isChatLoading}
        />
      )}
      {panelMode === 'WAITING_ON_DELEGATE' && (
        <WaitingOnDelegateContent
          planId={planId}
          step={step}
          participants={participants}
          feedItems={feedItems}
        />
      )}
      {(panelMode === 'WAITING_EXTERNAL' || panelMode === 'BLOCKED_CONNECTOR') && (
        <WaitingExternalContent planId={planId} step={step} />
      )}
      {panelMode === 'BLOCKED_CONNECTION_REQUIRED' && (
        <ConnectionRequiredContent planId={planId} step={step} />
      )}
      {panelMode === 'READY_TO_COMPLETE' && (
        <ReadyToCompleteContent
          step={step}
          feedItems={feedItems}
          onMarkComplete={handleMarkComplete}
        />
      )}
      {panelMode === 'COMPLETED' && (
        <CompletedContent
          step={step}
          feedItems={feedItems}
          onUndo={handleMarkComplete}
          nextStep={nextIncompleteStep}
          onNavigateToStep={onSelectStep}
        />
      )}

      {/* Show subplan link for non-ACTIVE_WORK modes when step has expansion */}
      {panelMode !== 'ACTIVE_WORK' && step.expansionPlanId && (
        <ExpandStepButton
          planId={planId}
          stepId={step.id}
          stepTitle={step.title}
          expansionPlanId={step.expansionPlanId}
        />
      )}

      {/* Assistive Chat Bar - demoted, secondary styling */}
      {/* Only show in ACTIVE_WORK mode when ActionPrompt is NOT shown */}
      {/* ActionPrompt handles its own completion UI for simple action steps */}
      {panelMode === 'ACTIVE_WORK' && !shouldShowActionPrompt(step) && (
        <CopilotInputBar
          onSend={handleCopilotSend}
          onMarkComplete={handleMarkComplete}
          isCompleted={isCompleted}
          isSending={isChatLoading}
          placeholder="Ask for help, add context, or upload files..."
          disabled={step.intake != null}
          hideMarkComplete={true}
        />
      )}

    </Box>
  );
};

export default StepWorkspacePane;
